import HelpIcon from '@mui/icons-material/Help';
import { Box, Link, Stack, type Theme, useMediaQuery } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import PageContainer from '/@/components/bayer/page-container';
import Tabs from '/@/components/bayer/tabs';
import { useAdvisorRvLinks, useOwnUserNode, useUserNodes } from '/@/hooks/nodes';
import { useIsValidatedRV } from '/@/hooks/session';
import { _ } from '/@/locale';
import { mpTrackEvent } from '/@/store/tracking/mixpanel-events';
import { objectEntries } from '/@/utils';
import Header from '../../../components/bayer/header';
import { type GrowersTabType, useTabs } from '..';
import AncestorsBar from '../components/ancestors-bar';
import RVOnboardingModalAdvisorSelector from '../rv/components/onboarding-modal-and-alert/advisor-selector';
import GrowersHelpTourDriver from './growers-help-tour/GrowersHelpTourDriver';
import { useGrowersHelpTour } from './growers-help-tour/useGrowersHelpTour';
import { GROWERS_HELP_TOUR_IDS } from './growers-help-tour-ids';
import { CurrentCampaignDashboard } from './tabs/current-campaign';
import { PastReportsDashboard } from './tabs/past-campaign';
import { RecommendationsDashboard } from './tabs/recommendations';

const GrowersListDashboard = () => {
    const { tab, handleTabChange } = useTabs();
    const isSmOrLower = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
    const [newGrowersModalOpen, setNewGrowersModalOpen] = useState(false);
    const [newFieldGrowersModalOpen, setNewFieldGrowersModalOpen] = useState(false);
    const [newFieldModalGrowerId, setNewFieldModalGrowerId] = useState<number | undefined>(undefined);

    const rvUserNodeQuery = useOwnUserNode();

    const ensureCurrentCampaignTab = useCallback(() => {
        handleTabChange('current-campaign');
    }, [handleTabChange]);

    const { growersHelpTourSteps, prepareGrowersHelpTour, startHelpTourFromIcon } = useGrowersHelpTour({
        onEnsureCurrentCampaignTab: ensureCurrentCampaignTab,
        canStartManualTour: Boolean(rvUserNodeQuery.data),
    });

    const atLeastOneGrowerExistsQuery = useUserNodes(
        {
            node: rvUserNodeQuery.data?.id,
            page: 1,
            page_size: 1,
            type: 'grower',
        },
        { enabled: Boolean(rvUserNodeQuery.data?.id) }
    );

    const [openRvOnboardingModalAdvisorSelector, setOpenRvOnboardingModalAdvisorSelector] = useState(false);
    const isValidatedRV = useIsValidatedRV();

    const tabs: Record<
        GrowersTabType,
        { label: string; disabled: boolean; component?: React.ReactNode; dataTestId?: string }
    > = {
        'current-campaign': {
            label: _('Campaña actual'),
            disabled: false,
            component: (
                <CurrentCampaignDashboard
                    newGrowersModalOpen={newGrowersModalOpen}
                    setNewGrowersModalOpen={setNewGrowersModalOpen}
                    newFieldGrowersModalOpen={newFieldGrowersModalOpen}
                    setNewFieldGrowersModalOpen={setNewFieldGrowersModalOpen}
                    newFieldModalGrowerId={newFieldModalGrowerId}
                    setNewFieldModalGrowerId={setNewFieldModalGrowerId}
                    rvUserNodeQuery={rvUserNodeQuery}
                />
            ),
        },
        'past-reports': {
            label: _('Reportes de campaña'),
            disabled: false,
            component: <PastReportsDashboard rvUserNodeQuery={rvUserNodeQuery} />,
        },
        recommendations: {
            label: _('Recomendaciones'),
            disabled: false,
            dataTestId: 'growers-recommendations-tab',
            component: <RecommendationsDashboard rvUserNodeQuery={rvUserNodeQuery} />,
        },
    } as const;

    const advisorRVLinksQuery = useAdvisorRvLinks();
    const advisors = useMemo(() => {
        return (advisorRVLinksQuery.data?.results ?? [])
            .filter((link) => link.rv.id === rvUserNodeQuery.data?.id)
            .map((link) => link.advisor);
    }, [advisorRVLinksQuery.data, rvUserNodeQuery.data?.id]);

    return (
        <PageContainer
            header={
                <Header
                    title={_('Mis Productores')}
                    titleIcon={
                        <HelpIcon
                            color='primary'
                            sx={{
                                width: 24,
                                height: 24,
                                cursor: rvUserNodeQuery.data ? 'pointer' : 'default',
                            }}
                            onClick={startHelpTourFromIcon}
                        />
                    }
                    subtitle={_('Potenciá las decisiones de tus productores y el rendimiento de sus campos.')}
                    buttons={[
                        {
                            children: _('Agregar lote'),
                            variant: 'outlined',
                            color: 'secondary',
                            size: 'medium',
                            onClick: () => {
                                setNewFieldModalGrowerId(undefined);
                                setNewFieldGrowersModalOpen(true);
                            },
                            disabled:
                                atLeastOneGrowerExistsQuery.isValidating ||
                                atLeastOneGrowerExistsQuery.data?.count === 0,
                        },
                        {
                            id: GROWERS_HELP_TOUR_IDS.ADD_GROWER,
                            children: _('Agregar productor'),
                            variant: 'contained',
                            color: 'secondary',
                            size: 'medium',
                            onClick: () => {
                                mpTrackEvent('Grower Creation flow:Started', {});
                                setNewGrowersModalOpen(true);
                            },
                        },
                    ]}
                />
            }
            boxChildren={
                <RVOnboardingModalAdvisorSelector
                    open={openRvOnboardingModalAdvisorSelector}
                    setOpen={setOpenRvOnboardingModalAdvisorSelector}
                />
            }
        >
            <GrowersHelpTourDriver
                steps={growersHelpTourSteps}
                isLoading={rvUserNodeQuery.isValidating || !rvUserNodeQuery.data}
                onBeforeAutoStart={prepareGrowersHelpTour}
            />
            {rvUserNodeQuery.data?.id && (
                <AncestorsBar ancestorsFromNodeId={rvUserNodeQuery.data?.id} additionalNodes={advisors}>
                    {isValidatedRV && advisors.length === 0 && !advisorRVLinksQuery.isValidating && (
                        <Link
                            color='secondary'
                            component='button'
                            onClick={() => {
                                setOpenRvOnboardingModalAdvisorSelector(true);
                            }}
                        >
                            {_('Vincularme con Asesores')}
                        </Link>
                    )}
                </AncestorsBar>
            )}

            <Stack direction='column' spacing={3} mt={4}>
                <Box id={GROWERS_HELP_TOUR_IDS.TABS}>
                    <Tabs
                        options={objectEntries(tabs).map(([value, tab]) => ({
                            label: tab.label,
                            value,
                            disabled: tab.disabled,
                            dataTestId: tab.dataTestId,
                        }))}
                        value={tab}
                        setValue={(value) => handleTabChange(value)}
                        tabsVariant={isSmOrLower ? 'scrollable' : 'fullWidth'}
                    />
                </Box>
                {tabs[tab].component}
            </Stack>
        </PageContainer>
    );
};

export default GrowersListDashboard;
